import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, MessageSquareText, Plus, Settings2, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";

import { fetchAppSetting, upsertAppSetting } from "@/lib/api/admin";
import { fetchTeams } from "@/lib/api/teams";

export const Route = createFileRoute("/_authenticated/admin/system-settings")({
  component: SystemSettingsRoute,
});

function SystemSettingsRoute() {
  return (
    <RequirePermission
      action="manage:users"
      fallback={<AccessDenied message="You don't have permission to manage system settings." />}
    >
      <SystemSettingsPage />
    </RequirePermission>
  );
}

function SystemSettingsPage() {
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Settings2 className="h-7 w-7 text-primary" />
          System Settings
        </h1>
        <p className="text-muted-foreground">Configure the student welcome message.</p>
      </div>

      <WelcomeMessageCard />
    </div>
  );
}

/* ── Welcome message ─────────────────────────────────────────────────────── */

/**
 * One per-project override. Every field is optional on purpose: the home dashboard resolves each
 * field independently (`override?.[k] ?? default[k]` — src/routes/_authenticated/home.tsx:159), so
 * an override may set only `title` and inherit the rest. An absent field means "inherit"; an empty
 * string would mean "render nothing", which is why blank inputs are omitted rather than saved.
 */
interface WelcomeOverride {
  title?: string;
  body1?: string;
  body2?: string;
  note?: string;
}

interface WelcomeMessage {
  title?: string;
  body1?: string;
  body2?: string;
  note?: string;
  byProject?: Record<string, WelcomeOverride>;
}

/** Editing shape: strings are always present so the inputs stay controlled. */
interface OverrideDraft {
  project: string;
  title: string;
  body1: string;
  body2: string;
  note: string;
}

type OverrideField = keyof Omit<OverrideDraft, "project">;

/**
 * Collapse the editing drafts back into the stored `byProject` map, dropping anything that would be
 * meaningless to persist: blank project names, blank fields (which must inherit), and entries that
 * end up carrying no fields at all.
 */
function buildByProject(drafts: OverrideDraft[]): Record<string, WelcomeOverride> {
  const out: Record<string, WelcomeOverride> = {};
  for (const draft of drafts) {
    const project = draft.project.trim();
    if (!project) continue;

    const entry: WelcomeOverride = {};
    if (draft.title.trim()) entry.title = draft.title.trim();
    if (draft.body1.trim()) entry.body1 = draft.body1.trim();
    if (draft.body2.trim()) entry.body2 = draft.body2.trim();
    if (draft.note.trim()) entry.note = draft.note.trim();

    if (Object.keys(entry).length > 0) out[project] = entry;
  }
  return out;
}

function WelcomeMessageCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["app_settings", "welcome_message"],
    queryFn: () => fetchAppSetting("welcome_message") as Promise<WelcomeMessage | null>,
  });

  // Same query key as the students page so the group list is fetched once and shared.
  const { data: teams = [] } = useQuery({
    queryKey: ["admin-teams-list"],
    queryFn: fetchTeams,
  });

  const [title, setTitle] = useState("");
  const [body1, setBody1] = useState("");
  const [body2, setBody2] = useState("");
  const [note, setNote] = useState("");
  const [overrides, setOverrides] = useState<OverrideDraft[]>([]);
  const [newProject, setNewProject] = useState("");
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  useEffect(() => {
    if (data) {
      setTitle(data.title ?? "");
      setBody1(data.body1 ?? "");
      setBody2(data.body2 ?? "");
      setNote(data.note ?? "");
      setOverrides(
        Object.entries(data.byProject ?? {}).map(([project, override]) => ({
          project,
          title: override?.title ?? "",
          body1: override?.body1 ?? "",
          body2: override?.body2 ?? "",
          note: override?.note ?? "",
        })),
      );
    }
  }, [data]);

  const takenProjects = useMemo(() => new Set(overrides.map((o) => o.project.trim())), [overrides]);

  const suggestedTeams = useMemo(
    () =>
      teams.filter(
        (t) =>
          t.name.toLowerCase().includes(newProject.trim().toLowerCase()) &&
          !takenProjects.has(t.name),
      ),
    [teams, newProject, takenProjects],
  );

  function addOverride(project: string) {
    const name = project.trim();
    if (!name) return;
    // An exact-match duplicate would silently replace the existing entry when the map is rebuilt.
    if (takenProjects.has(name)) {
      toast.error('An override for "' + name + '" already exists.');
      return;
    }
    setOverrides((prev) => [...prev, { project: name, title: "", body1: "", body2: "", note: "" }]);
    setNewProject("");
    setShowProjectDropdown(false);
  }

  function updateOverride(index: number, field: OverrideField, value: string) {
    setOverrides((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)),
    );
  }

  function removeOverride(index: number) {
    setOverrides((prev) => prev.filter((_, i) => i !== index));
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const byProject = buildByProject(overrides);
      return upsertAppSetting("welcome_message", {
        title: title.trim(),
        body1: body1.trim(),
        body2: body2.trim(),
        note: note.trim(),
        // Omit the key entirely when there is nothing to store, rather than persisting `{}`.
        ...(Object.keys(byProject).length > 0 ? { byProject } : {}),
      });
    },
    onSuccess: () => {
      toast.success("Welcome message saved");
      qc.invalidateQueries({ queryKey: ["app_settings", "welcome_message"] });
    },
    onError: () => toast.error("Failed to save welcome message"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquareText className="h-5 w-5 text-primary" />
          Student Welcome Message
        </CardTitle>
        <CardDescription>
          Shown on the student home dashboard. Markdown (e.g. **bold**) is supported in the body.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Welcome!"
              />
            </div>
            <div className="space-y-2">
              <Label>Body (paragraph 1)</Label>
              <Textarea value={body1} onChange={(e) => setBody1(e.target.value)} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Body (paragraph 2)</Label>
              <Textarea value={body2} onChange={(e) => setBody2(e.target.value)} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
            </div>

            <div className="border-t pt-4 space-y-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Per-project overrides
                </h3>
                <p className="text-xs text-muted-foreground">
                  A student whose assigned project matches an entry below sees that text instead.
                  Leave a field empty to inherit the default above.
                </p>
              </div>

              {overrides.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">
                  No per-project overrides configured.
                </p>
              ) : (
                overrides.map((entry, index) => (
                  <div key={entry.project} className="rounded-md border p-3 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium break-all">{entry.project}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOverride(index)}
                        aria-label={"Remove override for " + entry.project}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Title</Label>
                      <Input
                        value={entry.title}
                        onChange={(e) => updateOverride(index, "title", e.target.value)}
                        placeholder={title || "Inherits the default"}
                        aria-label={"Title override for " + entry.project}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Body (paragraph 1)</Label>
                      <Textarea
                        value={entry.body1}
                        onChange={(e) => updateOverride(index, "body1", e.target.value)}
                        placeholder={body1 || "Inherits the default"}
                        rows={2}
                        aria-label={"Body paragraph 1 override for " + entry.project}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Body (paragraph 2)</Label>
                      <Textarea
                        value={entry.body2}
                        onChange={(e) => updateOverride(index, "body2", e.target.value)}
                        placeholder={body2 || "Inherits the default"}
                        rows={2}
                        aria-label={"Body paragraph 2 override for " + entry.project}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Note</Label>
                      <Textarea
                        value={entry.note}
                        onChange={(e) => updateOverride(index, "note", e.target.value)}
                        placeholder={note || "Inherits the default"}
                        rows={2}
                        aria-label={"Note override for " + entry.project}
                      />
                    </div>
                  </div>
                ))
              )}

              <div className="space-y-2">
                <Label htmlFor="new-project-override">Add override for project</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="new-project-override"
                      value={newProject}
                      onChange={(e) => setNewProject(e.target.value)}
                      placeholder="Project / group name"
                    />
                    <button
                      type="button"
                      onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                      className="absolute right-2 top-2 h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer"
                      aria-label="Browse projects"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    {showProjectDropdown && (
                      <div className="absolute z-50 left-0 right-0 mt-1 max-h-40 overflow-y-auto bg-popover text-popover-foreground border rounded-md shadow-md py-1">
                        {suggestedTeams.map((team) => (
                          <button
                            key={team.id}
                            type="button"
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition cursor-pointer"
                            onClick={() => addOverride(team.name)}
                          >
                            {team.name}
                          </button>
                        ))}
                        {suggestedTeams.length === 0 && (
                          <p className="px-3 py-1.5 text-xs text-muted-foreground">
                            No matching projects
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => addOverride(newProject)}
                    disabled={!newProject.trim()}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              </div>
            </div>

            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving…" : "Save welcome message"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
